import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import BASE_URL from "../../config/config";

// تابع تعیین رنگ دسته‌بندی (برای تگ داخل کارت مقاله)
const getCategoryColor = (name) => {
  const colors = {
    "هوش مصنوعی": "bg-blue-100 text-blue-800",
    Python: "bg-green-100 text-green-800",
    جاوااسکریپت: "bg-yellow-100 text-yellow-800",
    "توسعه وب": "bg-red-100 text-red-800",
    default: "bg-gray-100 text-gray-700",
  };
  return colors[name] || colors.default;
};

// تابع تعیین آیکون دسته‌بندی
const getCategoryIcon = (name) => {
  const icons = {
    "هوش مصنوعی": "🤖",
    Python: "🐍",
    جاوااسکریپت: "📊",
    "توسعه وب": "🌐",
    "هوش مصنوعی عمومی": "🧠",
    default: "📚",
  };
  return icons[name] || icons.default;
};

// تابع تعیین رنگ کارت دسته‌بندی
const getCategoryCardColor = (name) => {
  const colors = {
    "هوش مصنوعی": { bg: "blue", text: "blue-800", border: "blue-100" },
    Python: { bg: "green", text: "green-800", border: "green-100" },
    جاوااسکریپت: { bg: "yellow", text: "yellow-800", border: "yellow-100" },
    "توسعه وب": { bg: "red", text: "red-800", border: "red-100" },
    default: { bg: "gray", text: "gray-700", border: "gray-100" },
  };
  return colors[name] || colors.default;
};

export default function ArticlesPage({
  categoryId = null,
  limit = null,
  tag = null,
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // فیلترها
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("همه دسته‌ها");

  // مدیریت نمایش مقالات
  const [visibleCount, setVisibleCount] = useState(3);
  const increment = 3;

  // دسته‌بندی‌های محبوب
  const [categoriesData, setCategoriesData] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // استفاده از هوک‌های react-router-dom
  const { categorySlug } = useParams();
  const navigate = useNavigate();

  // بارگذاری مقالات
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        let url;

        if (categorySlug) {
          // استفاده از endpoint جدید برای دسته‌بندی بر اساس slug
          url = `${BASE_URL}/blog/categories/${categorySlug}?status=published`;
        } else {
          url = `${BASE_URL}/blog/posts?status=published`;

          if (categoryId) {
            url += `&category_id=${categoryId}`;
          }
        }
        if (tag) {
          url += `&tag=${tag}`;
        }
        if (limit) {
          url += `&limit=${limit}`;
        }

        const response = await fetch(url, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("خطا در دریافت پست‌ها");
        }

        const result = await response.json();
        const fetchedPosts = result.data || [];

        const formattedPosts = fetchedPosts.map((post) => ({
          id: post.id,
          title: post.title,
          excerpt: post.summary || "این مقاله خلاصه‌ای ندارد.",
          category: post.category_name || post.category?.name || "متفرقه",
          date: new Date(post.created_at).toLocaleDateString("fa-IR"),
          readTime: post.reading_time
            ? `${post.reading_time} دقیقه`
            : "۵ دقیقه",
          views: post.views?.toLocaleString("fa-IR") || "۰",
          likes: post.likes?.toLocaleString("fa-IR") || "۰",
          author:
            post.author_name ||
            post.author?.full_name ||
            "نویسنده کارشناس سایت",
          avatar: post.author_avatar ? `/${post.author_avatar}` : "👤",
          slug: post.slug,
          cover_image: post.cover_image,
          featured: post.featured || false,
          tags: post.tags || post.tag_names?.split(",") || [],
        }));

        setPosts(formattedPosts);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [categoryId, categorySlug, limit, tag, categoriesData]);

  // بارگذاری دسته‌بندی‌های محبوب از API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await fetch(
          `${BASE_URL}/blog/categories?limit=100&sortBy=post_count&sortOrder=DESC`,
          {
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error("خطا در دریافت دسته‌بندی‌ها");

        const result = await response.json();
        const categories = result.rows || [];

        const formattedCategories = categories
          .map((cat) => ({
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            description: cat.description,
            postCount: cat.post_count || 0,
            icon: getCategoryIcon(cat.name),
            color: getCategoryCardColor(cat.name),
          }))
          .slice(0, 4); // فقط ۴ تا اول بر اساس تعداد مقالات

        setCategoriesData(formattedCategories);
      } catch (err) {
        console.error("خطا در بارگیری دسته‌بندی‌ها:", err);
        // در صورت خطا، داده پیش‌فرض نمایش داده می‌شود
        setCategoriesData([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // تابع تغییر دسته‌بندی
  const handleCategoryChange = (categoryName, categorySlug) => {
    setSelectedCategory(categoryName);

    if (categoryName === "همه دسته‌ها") {
      navigate("/blog");
    } else {
      navigate(`/blog/category/${categorySlug}`);
    }
  };

  // فیلتر مقالات
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.includes(searchTerm) || post.excerpt.includes(searchTerm);
    const matchesCategory =
      selectedCategory === "همه دسته‌ها" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // جدا کردن مقاله ویژه
  const featuredPost = filteredPosts.find((p) => p.featured);
  const regularPosts = filteredPosts.filter((p) => !p.featured);

  // مقالات نمایش داده شده
  const displayedRegularPosts = regularPosts.slice(0, visibleCount);

  // وضعیت دکمه‌ها
  const hasMorePosts = visibleCount < regularPosts.length;
  const canShowLess = visibleCount > 3;

  // هندلر دکمه‌ها
  const loadMorePosts = () => {
    setVisibleCount((prev) => Math.min(prev + increment, regularPosts.length));
  };

  const showLessPosts = () => {
    setVisibleCount(3);
  };

  // لیست دسته‌بندی‌ها (بدون نویسندگان)
  const categories = ["همه دسته‌ها", ...new Set(posts.map((p) => p.category))];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-lg text-blue-600">در حال بارگذاری مقالات...</div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-600">❌ {error}</div>;
  }

  return (
    <div className="bg-white text-right font-vazirmatn" dir="rtl">
      {/* Hero Section */}
      <section
        className="py-20 md:py-32 text-white"
        style={{
          background: "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            مقالات تخصصی
            <span className="block text-[#d6f0fa]">هوش جدید</span>
          </h1>
          <p className="text-xl md:text-2xl opacity-95 mb-8 max-w-4xl mx-auto leading-relaxed">
            آخرین مطالب و تحلیل‌های عمیق در حوزه هوش مصنوعی
            <br className="hidden md:block" />
            از متخصصان برتر صنعت تکنولوژی
          </p>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-12 bg-[#d6f0fa]">
        <div className="container mx-auto px-4">
          <div className="bg-white p-6 rounded-2xl shadow-lg max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2
                className="text-2xl font-bold mb-4"
                style={{
                  background:
                    "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                جستجو و فیلتر مقالات
              </h2>
              <p className="text-gray-600">مقاله مورد نظر خود را پیدا کنید</p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="جستجو در مقالات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 border-2 border-blue-200 rounded-full text-right focus:outline-none focus:border-[#00a0dc] focus:ring-3 focus:ring-blue-100 transition-all shadow-md"
              />
            </div>

            <div>
              <h3 className="font-bold text-[#140a46] mb-3">دسته‌بندی</h3>
              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      if (cat === "همه دسته‌ها") {
                        handleCategoryChange("همه دسته‌ها", "");
                      } else {
                        const category = categoriesData.find(
                          (c) => c.name === cat
                        );
                        if (category) {
                          handleCategoryChange(cat, category.slug);
                        }
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat
                        ? "bg-[#00a0dc] text-white border-[#00a0dc]"
                        : "bg-white border border-gray-300 text-gray-600 hover:border-[#00a0dc] hover:text-[#00a0dc]"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredPost && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2
                className="text-2xl font-bold mb-4"
                style={{
                  background:
                    "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                مقاله ویژه
              </h2>
              <p className="text-gray-600">آخرین و مهم‌ترین مقالات تخصصی</p>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-transparent hover:border-[#00a0dc] transition-all transform hover:-translate-y-3 duration-300 md:flex">
              <div className="md:w-1/2 relative">
                {featuredPost.cover_image ? (
                  <img
                    src={`/uploads/blog/posts/${featuredPost.cover_image}`}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-64 md:h-full flex items-center justify-center text-6xl"
                    style={{
                      background:
                        "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                    }}
                  >
                    📄
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 text-[#00a0dc] px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm">
                  ویژه
                </div>
              </div>
              <div className="md:w-1/2 p-8">
                <div
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold mb-4 ${getCategoryColor(
                    featuredPost.category
                  )}`}
                >
                  📂 {featuredPost.category}
                </div>
                <h2 className="text-3xl font-bold text-[#140a46] mb-4">
                  {featuredPost.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
                  <span>📅 {featuredPost.date}</span>
                  <span>⏱️ {featuredPost.readTime}</span>
                  <span>👁️ {featuredPost.views} بازدید</span>
                  <span>❤️ {featuredPost.likes}</span>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  {typeof featuredPost.avatar === "string" &&
                  featuredPost.avatar.startsWith("/") ? (
                    <img
                      src={featuredPost.avatar}
                      alt={featuredPost.author}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{
                        background:
                          "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                      }}
                    >
                      {featuredPost.avatar}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-[#140a46]">
                      {featuredPost.author}
                    </p>
                    <p className="text-xs text-gray-500">نویسنده</p>
                  </div>
                </div>
                <Link
                  to={`/blog/posts/${featuredPost.slug}`}
                  className="bg-[#00a0dc] hover:bg-[#140a46] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg transform hover:-translate-y-1 inline-block"
                >
                  📖 مطالعه مقاله
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                background: "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              مقالات تخصصی
            </h2>
            <p className="text-gray-600 text-lg">
              آخرین مطالب در حوزه هوش مصنوعی و تکنولوژی
            </p>
          </div>

          <p className="text-gray-600 mb-8">
            {filteredPosts.length} مقاله یافت شد
            {selectedCategory !== "همه دسته‌ها" && (
              <span> در دسته‌بندی "{selectedCategory}"</span>
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedRegularPosts.length === 0 ? (
              <div className="col-span-full text-center py-10 text-gray-500">
                هیچ مقاله‌ای با این فیلترها یافت نشد.
              </div>
            ) : (
              displayedRegularPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-3xl overflow-hidden shadow-lg border border-transparent hover:border-[#00a0dc] transition-all transform hover:-translate-y-3 duration-300"
                >
                  <div className="relative">
                    {post.cover_image ? (
                      <img
                        src={`/uploads/blog/posts/${post.cover_image}`}
                        alt={post.title}
                        className="w-full h-48 "
                      />
                    ) : (
                      <div
                        className="w-full h-48 flex items-center justify-center text-5xl"
                        style={{
                          background:
                            "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                        }}
                      >
                        📄
                      </div>
                    )}
                    {post.featured && (
                      <div className="absolute top-3 right-3 bg-white/90 text-[#00a0dc] px-2 py-1 rounded-full text-xs font-bold backdrop-blur-sm">
                        ویژه
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold mb-3 ${getCategoryColor(
                        post.category
                      )}`}
                    >
                      📂 {post.category}
                    </div>
                    <h3 className="text-xl font-bold text-[#140a46] mb-3">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-4">
                      <span>📅 {post.date}</span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                        {post.readTime}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>👁️ {post.views}</span>
                      <span>❤️ {post.likes}</span>
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                      {typeof post.avatar === "string" &&
                      post.avatar.startsWith("/") ? (
                        <img
                          src={post.avatar}
                          alt={post.author}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                          style={{
                            background:
                              "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                          }}
                        >
                          {post.avatar}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#140a46] text-sm">
                          {post.author}
                        </p>
                        <p className="text-xs text-gray-500">نویسنده</p>
                      </div>
                    </div>
                    <Link
                      to={`/blog/posts/${post.slug}`}
                      className="w-full bg-[#00a0dc] hover:bg-[#140a46] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg transform hover:-translate-y-1 inline-block text-center"
                    >
                      📖 مطالعه کامل
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-center mt-12 space-y-4">
            {hasMorePosts && (
              <button
                onClick={loadMorePosts}
                className="bg-[#00a0dc] hover:bg-[#140a46] text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-lg transform hover:-translate-y-1"
              >
                📰 مشاهده مقالات بیشتر
              </button>
            )}

            {canShowLess && (
              <button
                onClick={showLessPosts}
                className="border-2 border-[#00a0dc] text-[#00a0dc] hover:bg-[#d6f0fa] px-10 py-4 rounded-xl font-bold text-lg transition-all"
              >
                🔽 کم کردن مقالات
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Popular Categories - Dynamic from API */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                background: "linear-gradient(135deg, #00a0dc 0%, #140a46 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              دسته‌بندی‌های محبوب
            </h2>
            <p className="text-gray-600 text-lg">موضوعات پرطرفدار مقالات</p>
          </div>

          {categoriesLoading ? (
            <div className="text-center py-10 text-gray-500">
              در حال بارگذاری دسته‌بندی‌ها...
            </div>
          ) : categoriesData.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              هیچ دسته‌بندی‌ای یافت نشد.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {categoriesData.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.name, cat.slug)}
                  className={`text-center p-6 bg-${cat.color.bg}-50 rounded-2xl hover:shadow-lg transition-all cursor-pointer transform hover:-translate-y-1 border border-${cat.color.border}`}
                >
                  <div className="text-4xl mb-4">{cat.icon}</div>
                  <h3
                    className={`text-xl font-bold text-${cat.color.text} mb-2`}
                  >
                    {cat.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    مقالات تخصصی و آموزشی
                  </p>
                  <div
                    className={`text-2xl font-bold text-${cat.color.bg}-700`}
                  >
                    {cat.postCount} مقاله
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
